import { useEffect, useMemo, useState } from 'react';
import {
    Building2, Image as ImageIcon, Mail, FileText, SlidersHorizontal,
    CheckCircle2, Circle, AlertTriangle, ArrowRight, Loader2, ExternalLink,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { API_CONFIG } from '@/config/config.js';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Setup overview — the landing tab.
 *
 * Answers the question a company admin actually arrives with: is this
 * configured properly, and what still needs my attention? Each incomplete item
 * says what the consequence is rather than just naming the field, and links
 * straight to the tab that fixes it.
 */

/** Progress ring. Plain SVG — no chart library for one number. */
function CompletionRing({ percent, size = 52 }) {
    const stroke = 4;
    const r = (size - stroke) / 2;
    const circumference = 2 * Math.PI * r;
    const done = percent >= 100;

    return (
        <svg width={size} height={size} className="shrink-0 -rotate-90" role="img"
            aria-label={`Setup ${percent}% complete`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
                className="stroke-muted" />
            <circle
                cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke}
                strokeLinecap="round"
                className={cn('transition-all duration-700', done ? 'stroke-emerald-500' : 'stroke-primary')}
                strokeDasharray={circumference}
                strokeDashoffset={circumference - (circumference * percent) / 100}
            />
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
                className="rotate-90 fill-foreground text-[11px] font-semibold"
                style={{ transformOrigin: 'center' }}>
                {percent}%
            </text>
        </svg>
    );
}

const STATE_STYLES = {
    done:    { icon: CheckCircle2,   cls: 'text-emerald-500', border: 'border-border' },
    todo:    { icon: Circle,         cls: 'text-muted-foreground/50', border: 'border-border' },
    warning: { icon: AlertTriangle,  cls: 'text-amber-500', border: 'border-amber-500/30' },
};

function ChecklistRow({ state, icon: Icon, title, detail, actionLabel, onAction }) {
    const s = STATE_STYLES[state];
    return (
        <div className={cn(
            'flex items-center gap-3 rounded-xl border bg-card px-4 py-3 transition-colors',
            s.border,
            state !== 'done' && 'hover:border-primary/30',
        )}>
            <s.icon className={cn('h-4.5 w-4.5 shrink-0', s.cls)} style={{ width: 18, height: 18 }} />
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{title}</p>
                <p className="truncate text-xs text-muted-foreground">{detail}</p>
            </div>
            {state !== 'done' && (
                <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1 text-xs" onClick={onAction}>
                    {actionLabel}<ArrowRight className="h-3 w-3" />
                </Button>
            )}
        </div>
    );
}

export default function SettingsOverview({ onNavigate }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // Each call is independent — one failing endpoint should not blank
            // the whole overview.
            const [profile, emailCfg, templates, interview] = await Promise.allSettled([
                apiClient.getCompanyProfile(),
                apiClient.getCompanyEmailConfig(),
                apiClient.getEmailTemplates(),
                apiClient.getInterviewSettings(),
            ]);
            if (cancelled) return;

            const val = (r) => (r.status === 'fulfilled' ? r.value?.data : null);
            setData({
                profile:   val(profile),
                emailCfg:  val(emailCfg),
                templates: val(templates) || [],
                interview: val(interview),
            });
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const checks = useMemo(() => {
        if (!data) return [];
        const p   = data.profile?.profile || {};
        const cfg = data.emailCfg || {};
        const customised = data.templates.filter(t => !t.isDefault).length;

        return [
            {
                key: 'identity',
                state: data.profile?.companyName && p.about ? 'done' : 'todo',
                icon: Building2,
                title: 'Company name and description',
                detail: data.profile?.companyName && p.about
                    ? 'Shown on every job posting'
                    : 'Candidates see no context about who is hiring',
                actionLabel: 'Add',
                tab: 'company',
            },
            {
                key: 'logo',
                state: p.logoUrl ? 'done' : 'todo',
                icon: ImageIcon,
                title: 'Company logo',
                detail: p.logoUrl
                    ? 'Appears on job pages and in emails'
                    : 'Candidates see your initials until you upload one',
                actionLabel: 'Upload',
                tab: 'company',
            },
            {
                key: 'smtp',
                state: cfg.status === 'verified' ? 'done'
                     : cfg.status === 'failing'  ? 'warning' : 'todo',
                icon: Mail,
                title: 'Send email from your own domain',
                detail: cfg.status === 'verified' ? 'Candidate emails come from your mail server'
                      : cfg.status === 'failing'  ? 'Your mail server rejected a message — sending as EchoHire'
                      : cfg.status === 'unverified' ? 'Saved but not verified — send a test email to activate'
                      : 'Currently sending as EchoHire',
                actionLabel: cfg.status === 'unverified' ? 'Verify' : 'Set up',
                tab: 'email',
            },
            {
                key: 'templates',
                state: customised > 0 ? 'done' : 'todo',
                icon: FileText,
                title: 'Email wording',
                detail: customised > 0
                    ? `${customised} template${customised === 1 ? '' : 's'} written in your voice`
                    : 'Using EchoHire\'s standard wording',
                actionLabel: 'Review',
                tab: 'templates',
            },
            {
                key: 'threshold',
                state: data.interview?.passThreshold != null ? 'done' : 'todo',
                icon: SlidersHorizontal,
                title: 'Interview pass threshold',
                detail: data.interview?.passThreshold != null
                    ? `Candidates scoring ${data.interview.passThreshold}% or above pass a round`
                    : 'Not set',
                actionLabel: 'Set',
                tab: 'interview',
            },
        ];
    }, [data]);

    if (loading) {
        return (
            <div className="space-y-3">
                <div className="h-20 animate-pulse rounded-xl bg-muted/50" />
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-xl bg-muted/40" />
                ))}
            </div>
        );
    }

    const doneCount = checks.filter(c => c.state === 'done').length;
    const percent   = Math.round((doneCount / checks.length) * 100);
    const remaining = checks.filter(c => c.state !== 'done');
    const companyName = data.profile?.companyName || 'your company';
    const logoUrl = data.profile?.profile?.logoUrl;
    const logoSrc = logoUrl
        ? (logoUrl.startsWith('http') ? logoUrl : `${API_CONFIG.STATIC_BASE_URL}${logoUrl}`)
        : null;
    const monogram = companyName.split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

    return (
        <div className="space-y-6">
            {/* ── Progress ──────────────────────────────────────────────── */}
            <div className="flex items-center gap-4 rounded-xl bg-muted/40 px-4 py-4">
                <CompletionRing percent={percent} />
                <div className="min-w-0">
                    <p className="font-medium text-foreground">
                        {percent === 100
                            ? `${companyName} is fully set up`
                            : `Finish setting up ${companyName}`}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        {percent === 100
                            ? 'Everything candidates see is configured.'
                            : `${remaining.length} thing${remaining.length === 1 ? '' : 's'} left before candidates see your brand.`}
                    </p>
                </div>
            </div>

            {/* ── Checklist ─────────────────────────────────────────────── */}
            <div className="space-y-2">
                {checks.map(c => (
                    <ChecklistRow
                        key={c.key}
                        state={c.state}
                        icon={c.icon}
                        title={c.title}
                        detail={c.detail}
                        actionLabel={c.actionLabel}
                        onAction={() => onNavigate(c.tab)}
                    />
                ))}
            </div>

            {/* ── Candidate-facing preview ──────────────────────────────── */}
            <div className="rounded-xl bg-muted/40 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        What candidates see
                    </p>
                    <button type="button" onClick={() => onNavigate('company')}
                        className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
                        Edit<ExternalLink className="h-2.5 w-2.5" />
                    </button>
                </div>

                <div className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-center gap-3">
                        {logoSrc ? (
                            <img src={logoSrc} alt={companyName}
                                className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain bg-background p-1" />
                        ) : (
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold text-primary ring-1 ring-primary/15">
                                {monogram}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-foreground">{companyName}</p>
                            <p className="text-xs text-muted-foreground">is hiring</p>
                        </div>
                    </div>
                    {data.profile?.profile?.about && (
                        <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {data.profile.profile.about}
                        </p>
                    )}
                </div>

                <p className="mt-2.5 text-[11px] text-muted-foreground">
                    Emails are sent from{' '}
                    <strong className="text-foreground">
                        {data.emailCfg?.status === 'verified'
                            ? (data.emailCfg.fromEmail || data.emailCfg.authUser || 'your mail server')
                            : 'EchoHire'}
                    </strong>.
                </p>
            </div>
        </div>
    );
}
